<template>
  <section>
    <page-sub-header
      :title="portalUpdatesJan2026Enabled ? 'App Engagement' : 'App Logins'"
      :subtitle="isDataReady ? timeSeriesSubtitle : undefined"
      class="mt-4"
    >
      <template #header>
        <div class="d-flex flex-row align-items-center justify-content-end">
          <app-input
            v-model="timeSeries"
            :options="TIME_SERIES_VALUES"
            type="radio"
            buttons
            button-variant="outline-primary"
            class="text-uppercase mb-0"
            size="sm"
          />
          <export-button
            v-if="isDataReady"
            :file-name="`app-logins-metrics-${timeSeries}`"
            :export-data="exportData"
            :export-file-rows="exportFileRows"
            :export-headers="exportHeaders"
            class="btn mt-0"
          />
        </div>
      </template>
    </page-sub-header>
    <sme-card v-if="loading">
      <app-loading />
    </sme-card>
    <template v-else>
      <chart-with-companion-kpis
        v-if="timeGroupedUniqueUsersDataset.values.length"
        card-class="mb-4"
        chart-title="App logins over time"
        chart-subtitle="Number of logins"
      >
        <line-chart
          :labels="timeGroupDataset"
          :datasets="timeGroupedUniqueUsersDataset"
          :chart-options="{
            aspectRatio: getInitialAspectRatio(3, 2),
            scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedUniqueUsersDataset.name } } },
            ...createCountWithPercentageTooltip(timeGroupedUniqueUsersPercentageDataset),
          }"
          fill-gradient
        />
        <template #companion>
          <div class="stats-combined mb-3">
            <app-stat
              :stat="lastGroupedSession"
              :previous-stat="prevGroupedSession"
              :formatter="toInteger"
              :description="portalUpdatesJan2026Enabled ? 'Active Users' : 'App Logins'"
              background-color="core-1-lighten-90"
              color="default"
              pdf-kpi
            />
            <app-stat
              :stat="lastGroupedSessionPercentage"
              :previous-stat="prevGroupedSessionPercentage"
              :formatter="toNumber"
              suffix="%"
              :description="portalUpdatesJan2026Enabled ? 'Active User Rate' : 'Login Percentage'"
              background-color="core-1-lighten-90"
              color="default"
              pdf-kpi
            />
          </div>
          <p class="mb-0">
            {{ timeGroupTitle }}, {{ toInteger(lastGroupedSession) }} employees ({{ lastGroupedSessionPercentage }}%)
            logged in to the app.
            {{ sessionPrevDiffText }}
          </p>
        </template>
      </chart-with-companion-kpis>

      <b-row v-if="!portalUpdatesJan2026Enabled" class="mb-4">
        <b-col lg="4" class="mb-4 mb-lg-0">
          <sme-card v-pdf-chart-group class="h-100 overflow-hidden">
            <app-stat
              :stat="averageVisitsPerUserDataset.values[averageVisitsPerUserDataset.values.length - 1] || 0"
              :formatter="Math.round"
              :description="`Average app visits per user per ${timeSeriesUnit}`"
              :class="[averageVisitsPerUserDataset.values.length && 'mb-3']"
              background-color="core-5-lighten-90"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart title="Average app visits per user" layout="triple">
              <line-chart
                v-if="averageVisitsPerUserDataset.values.length"
                :labels="timeGroupDataset"
                :datasets="averageVisitsPerUserDataset"
                :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
        <b-col lg="4" class="mb-4 mb-lg-0">
          <sme-card v-pdf-chart-group class="h-100 overflow-hidden">
            <app-stat
              :stat="averageTimePerUserDataset.values[averageTimePerUserDataset.values.length - 1] || 0"
              :formatter="Math.round"
              :description="`Average minutes on app per user per ${timeSeriesUnit}`"
              :class="[averageTimePerUserDataset.values.length && 'mb-3']"
              background-color="core-4-lighten-90"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart title="Average minutes on app per user" layout="triple">
              <line-chart
                v-if="averageTimePerUserDataset.values.length"
                :labels="timeGroupDataset"
                :datasets="averageTimePerUserDataset"
                :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
        <b-col lg="4" class="mb-lg-0">
          <sme-card v-pdf-chart-group class="h-100 overflow-hidden">
            <app-stat
              :stat="uniqueDaysDataset.calculated?.last || 0"
              :formatter="Math.round"
              :description="
                portalUpdatesJan2026Enabled
                  ? `Average days on app per user per ${timeSeriesUnit}`
                  : `Average days per ${timeSeriesUnit} on app`
              "
              :class="[uniqueDaysDataset.values.length && 'mb-3']"
              background-color="core-3-lighten-80"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart title="Average unique days on app" layout="triple">
              <line-chart
                v-if="uniqueDaysDataset.values.length"
                :labels="timeGroupDataset"
                :datasets="uniqueDaysDataset"
                :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
      </b-row>
    </template>
  </section>
</template>

<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import { PaletteColors } from '@/Theme';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import ChartWithCompanionKpis from '@/components/ChartWithCompanionKpis.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import PdfChart from '@/components/PdfChart.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import LineChart, { CHART_PRESET_OPTIONS } from '@/components/charts/LineChart.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import { TIME_SERIES_VALUES, createCountWithPercentageTooltip, getInitialAspectRatio } from '@/utils/chart';
import { toInteger, toNumber } from '@/utils/common';
import ExportButton from '@/components/ExportButton.vue';

const { portalUpdatesJan2026Enabled } = useFeatureFlags();
const { divisionId } = useDivisionSelection();

const loading = ref(true);
const timeDataset = ref(null);
const uniqueUsersDataset = ref(null);
const uniqueUsersPercentageDataset = ref(null);
const averageVisitsPerUserDataset = ref(null);
const averageTimePerUserDataset = ref(null);
const uniqueDaysDataset = ref(null);

const {
  timeGroupDataset,
  timeSeries,
  timeSeriesUnit,
  timeSeriesSubtitle,
  timeGroupTitle,
  datasetToTimeGroupedDataset,
  getPrevDiffText,
} = useTimeGroupDataset(timeDataset);

const timeGroupedUniqueUsersDataset = computed(() => datasetToTimeGroupedDataset(uniqueUsersDataset));
const timeGroupedUniqueUsersPercentageDataset = computed(() =>
  datasetToTimeGroupedDataset(uniqueUsersPercentageDataset),
);
const lastGroupedSession = computed(() => timeGroupedUniqueUsersDataset.value?.calculated.last);
const prevGroupedSession = computed(() => timeGroupedUniqueUsersDataset.value?.calculated.prev);
const lastGroupedSessionPercentage = computed(() => timeGroupedUniqueUsersPercentageDataset.value?.calculated.last);
const prevGroupedSessionPercentage = computed(() => timeGroupedUniqueUsersPercentageDataset.value?.calculated.prev);
const sessionPrevDiffText = computed(() => getPrevDiffText(timeGroupedUniqueUsersDataset));

watch([divisionId, timeSeries], () => getAppLoginsMetrics());

const getAppLoginsMetrics = async () => {
  loading.value = true;

  const { results } = await ApiClient.getAppLoginsMetrics(timeSeries.value, divisionId.value);

  if (results?.length) {
    const time = results.find(result => result.id === 'time');
    const uniqueUsers = results.find(result => result.id === 'unique_users_in_app');
    const enrolled = results.find(result => result.id === 'enrolled');
    const sessions = results.find(result => result.id === 'sessions');
    const totalTime = results.find(result => result.id === 'total_time_on_app_seconds');
    const uniqueDays = results.find(result => result.id === 'unique_days_in_app');

    timeDataset.value = time;
    uniqueUsersDataset.value = {
      ...uniqueUsers,
      name: 'Number of logins',
    };
    uniqueUsersPercentageDataset.value = {
      name: 'Percentage (%)',
      values: enrolled.values.map((value, index) =>
        value ? Number(toNumber((uniqueUsers.values[index] / value) * 100)) : 0,
      ),
    };
    averageVisitsPerUserDataset.value = {
      name: 'Visits per user',
      values: uniqueUsers.values.map((value, index) => (value ? sessions.values[index] / value : 0)),
      lineColor: PaletteColors['core-5'],
    };
    averageTimePerUserDataset.value = {
      name: 'Minutes on app',
      values: uniqueUsers.values.map((value, index) => (value ? totalTime.values[index] / 60 / value : 0)),
      lineColor: PaletteColors['core-4'],
    };
    uniqueDaysDataset.value = {
      ...uniqueDays,
      name: 'Unique days on app',
      lineColor: PaletteColors['core-3'],
    };
  }

  loading.value = false;
};

onBeforeMount(() => getAppLoginsMetrics());

const isDataReady = computed(() => {
  return (
    timeGroupDataset.value?.values &&
    uniqueUsersDataset.value?.values &&
    uniqueUsersPercentageDataset.value?.values &&
    averageVisitsPerUserDataset.value?.values &&
    averageTimePerUserDataset.value?.values &&
    uniqueDaysDataset.value?.values &&
    !loading.value
  );
});

const exportData = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return timeGroupDataset.value.values.map((time, index) => ({
    time,
    logins: uniqueUsersDataset.value.values[index],
    percentage: uniqueUsersPercentageDataset.value.values[index],
    averageVisits: averageVisitsPerUserDataset.value.values[index],
    averageMinutes: averageTimePerUserDataset.value.values[index],
    uniqueDays: uniqueDaysDataset.value.values[index],
  }));
});

const exportHeaders = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return [
    'Date',
    uniqueUsersDataset.value.name,
    uniqueUsersPercentageDataset.value.name,
    'Average Visits per User',
    'Average Minutes per User',
    'Average Days per User',
  ];
});

const exportFileRows = item => [
  item.time,
  item.logins,
  item.percentage,
  Number(item.averageVisits.toFixed(2)),
  Number(item.averageMinutes.toFixed(2)),
  Number(item.uniqueDays.toFixed(2)),
];
</script>

<style lang="scss" scoped>
.stats-combined {
  display: flex;

  :deep(.app-stat) {
    flex: 1;
    height: auto; // Override height: 100% from AppStat to allow flexbox stretch
    border-radius: 0;
    box-shadow: none;

    &:first-child {
      border-radius: 4px 0 0 4px;
      border-right: 1px solid rgba(0, 0, 0, 0.1);
    }

    &:last-child {
      border-radius: 0 4px 4px 0;
    }

    .app-stat__value-container .app-stat__value {
      font-size: 28px;

      @media (min-width: 992px) {
        font-size: 32px;
      }
    }

    .app-stat__value-container {
      height: auto;
      min-height: 2rem;
    }
  }
}
</style>
